import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { Card, CardContent, createStyles, Divider, Grid, Theme, withStyles, WithStyles } from '@material-ui/core';
import {
  AccessTime as ClockIcon,
  DateRange as DateIcon,
  Domain as AddressIcon,
  EventNote as InstructionsIcon,
  Videocam as VideoIcon,
} from '@material-ui/icons';
import classnames from 'classnames';
import { ShowOnAvailabilitiesListEnum, TAppointmentBookedTimeZoneStorage } from 'core/AvailabilitySelector/types';
import { getAppointmentBookedTimeZoneStorageKey } from 'core/AvailabilitySelector/utils';
import { get } from 'lodash';
import moment from 'moment-timezone';

import { selectCurrentMasterSettings } from '@lib/selectors';
import { useCurrentLanguage } from '@lib/hooks';
import { Address, Appointment } from '@lib/resources/types';
import storage from '@lib/services/storageService';

import { useAddress, useAddToCalendarEvent, useSpecialInstructions } from 'hooks';

import AddToCalendar from './AddToCalendar';
import Typography from './Typography';
import TypographyWithIcon from './TypographyWithIcon';

const messages = defineMessages({
  ApptDetails: {
    id: 'components.v2.CardAppointmentDetails.ApptDetails',
    defaultMessage: 'Appointment Details',
  },
  providerName: {
    id: 'components.v2.CardAppointmentDetails.providerName',
    defaultMessage: '{name}',
  },
  instructions: {
    id: 'components.v2.CardAppointmentDetails.instructions',
    defaultMessage: 'Special instructions',
  },
  instruction1: {
    id: 'components.v2.CardAppointmentDetails.instruction1',
    defaultMessage: 'Please come fully hydrated (try to drink a cup or two of water an hour before the appointment)',
  },
  instruction2: {
    id: 'components.v2.CardAppointmentDetails.instruction2',
    defaultMessage:
      'Please arrive 30 minutes before your appointment to ensure we have enough time for a COVID screening',
  },
  telehealthAddress: {
    id: 'components.v2.CardAppointmentDetails.telehealthAddress',
    defaultMessage: 'Telehealth - Online appointment',
  },
});

const styles = ({ custom: { palette }, spacing: { unit } }: Theme) =>
  createStyles({
    infoItem: {
      marginBottom: unit * 3,
    },
    marginBotton: {
      marginBottom: unit * 2,
    },
    gridItem: {
      width: '100%',
    },
    baseCard: {
      boxShadow: '0px 4px 16px rgba(125, 125, 125, 0.25)',
      padding: unit,
    },
    icon: {
      color: `${palette.colorSupportSlate}`,
      fontSize: '20px',
    },
    iconMultipleOnLines: {
      marginTop: '2px',
      alignSelf: 'start',
    },
    nestedItem: {
      marginLeft: unit * 3.75,
    },
    specialInstructionList: {
      margin: 0,
    },
  });

export interface CardAppointmentDetailsProps extends WithStyles<typeof styles> {
  appointment: any;
}

const localTimeZoneIANA = Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();

const CardAppointmentDetails: React.FC<CardAppointmentDetailsProps> = ({ appointment, classes }) => {
  const language = useCurrentLanguage();
  const rootSettings = useSelector(selectCurrentMasterSettings);
  const { formatMessage } = useIntl();

  const address = useAddress(appointment && appointment.facility ? (appointment.facility as Address) : {});
  const event = useAddToCalendarEvent((appointment as unknown) as Appointment);
  const specialInstructions = useSpecialInstructions(appointment);

  const isTelehealth =
    get(appointment, 'type.modality') === 'telehealth' || get(appointment, 'appointmentType.modality') === 'telehealth';

  const isAddToCalendarEnabled = /^(confirmed)$|^(unconfirmed)$/.test(appointment.status);

  const appointmentBookedTimeZone: TAppointmentBookedTimeZoneStorage = storage.get(
    getAppointmentBookedTimeZoneStorageKey(get(appointment, 'id', '')),
  );
  const isToUseFacilityTimeZone = get(rootSettings, 'useTimezoneFrom', null) === 'facility';
  const defaultTimeZone = rootSettings?.timezone ?? localTimeZoneIANA;
  const appointmentTimezone =
    isToUseFacilityTimeZone && appointment?.facility?.settings?.timezone
      ? appointment?.facility?.settings?.timezone
      : defaultTimeZone;
  const appointmentDateInfoToShow =
    get(appointmentBookedTimeZone, 'setting', ShowOnAvailabilitiesListEnum.DoNotShow) !==
    ShowOnAvailabilitiesListEnum.DoNotShow
      ? {
          appointmentDate: moment(get(appointment, 'date', '')).tz(get(appointmentBookedTimeZone, 'IANA', '')),
          timeZoneAbbreviationToShow: new Date()
            .toLocaleDateString(language, {
              timeZone: get(appointmentBookedTimeZone, 'IANA', ''),
              timeZoneName: 'long',
              day: '2-digit',
            })
            .slice(4),
        }
      : {
          appointmentDate: moment(get(appointment, 'date', '')).tz(appointmentTimezone),
          timeZoneAbbreviationToShow: new Date()
            .toLocaleDateString(language, { timeZone: appointmentTimezone, timeZoneName: 'long', day: '2-digit' })
            .slice(4),
        };

  return (
    <Grid item className={classes.gridItem}>
      <Card elevation={0} className={classes.baseCard}>
        <CardContent>
          <div className={classes.marginBotton}>
            <Typography variant="bW600F17" color="supportSlate">
              {formatMessage(messages.ApptDetails)}
            </Typography>
          </div>
          <Divider variant="fullWidth" className={classes.marginBotton} />
          <div className={classes.infoItem}>
            <TypographyWithIcon
              icon={<DateIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />}
              variant="bW400F17"
            >
              {appointmentDateInfoToShow.appointmentDate.format('dddd, MMMM DD, YYYY')}
            </TypographyWithIcon>
          </div>
          <div className={classes.infoItem}>
            <TypographyWithIcon
              icon={<ClockIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />}
              variant="bW400F17"
            >
              {appointmentDateInfoToShow.appointmentDate.format('LT')}{' '}
              <Typography variant="bW600F17" inline>
                {appointmentDateInfoToShow.timeZoneAbbreviationToShow}
              </Typography>
            </TypographyWithIcon>
          </div>
          <div className={classes.infoItem}>
            <TypographyWithIcon
              icon={
                isTelehealth ? (
                  <VideoIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />
                ) : (
                  <AddressIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />
                )
              }
              variant="bW400F17"
            >
              {!isTelehealth && get(appointment, 'facility.name', '')}
              <Typography
                to={isTelehealth ? undefined : `https://www.google.com/maps/search/${address}`}
                variant="bW400F17"
                linkProps={{ target: '_blank' }}
                underline={!isTelehealth}
              >
                {isTelehealth ? formatMessage(messages.telehealthAddress) : address}
              </Typography>
              {!isTelehealth && <Typography variant="bW400F17">{get(appointment, 'facility.phone', '')}</Typography>}
            </TypographyWithIcon>
          </div>
          {specialInstructions.length > 0 && (
            <div className={classes.infoItem}>
              <TypographyWithIcon icon={<InstructionsIcon className={classes.icon} />} variant="bW400F17">
                {formatMessage(messages.instructions)}
              </TypographyWithIcon>
              <Typography className={classes.nestedItem} variant="bW400F17">
                <ul className={classes.specialInstructionList}>
                  {specialInstructions.map((instruction, idx) => (
                    <li key={`${instruction}-${idx + 1}`}>{instruction}</li>
                  ))}
                </ul>
              </Typography>
            </div>
          )}
          {isAddToCalendarEnabled && event && (
            <div className={classes.infoItem}>
              <AddToCalendar event={event} />
            </div>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default withStyles(styles)(CardAppointmentDetails);
